import styled from "styled-components";
import FlexBox, { FlexGrow } from "./FlexBox";
import { ClickableIcon } from "./Icon";
import Modal from "./Modal";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { ReactFlow, MarkerType, useReactFlow } from "@xyflow/react";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import CustomNode from "./CustomNode";
import { ZoomControls } from "./ZoomControl";
import { ReactFlowProvider } from "@xyflow/react";
import { SemesterContext } from "../context/SemesterContext";
import { UserContext } from "../context/UserContext";
import { click } from "@testing-library/user-event/dist/click";

type PrerequisiteChainModalProps = {
  isPrerequisiteModalOpen: boolean;
  setIsPrerequisiteModalOpen: (isOpen: boolean) => void;
  focusCompetency: string;
  focusId: string;
  selectedVersion: number | Number;
  role: any;
  setSelectedCompetency: (competency: string) => void;
};

type Competency = {
  title: any;
  name: string;
  prerequisites?: string[];
};

const Header = styled.h4`
  font-family: Montserrat;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: white;
`;

const CustomFlex = styled(FlexBox)`
  // padding: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
`;

function buildDependencyGraph(data: Competency[]): Map<string, number> {
  const graph = new Map();
  const inDegree = new Map();

  data.forEach(
    ({
      name,
      prerequisites = [],
    }: {
      name: string;
      prerequisites?: string[];
    }) => {
      graph.set(name, []);
      inDegree.set(name, prerequisites.length);
    }
  );

  data.forEach(
    ({
      name,
      prerequisites = [],
    }: {
      name: string;
      prerequisites?: string[];
    }) => {
      prerequisites.forEach((pre) => {
        if (!graph.has(pre)) graph.set(pre, []);
        graph.get(pre).push(name);
      });
    }
  );

  const levelMap = new Map();
  const queue: any = [];

  data.forEach(({ name }: { name: string }) => {
    if (inDegree.get(name) === 0) {
      queue.push(name);
      levelMap.set(name, 0);
    }
  });

  while (queue.length > 0) {
    const current = queue.shift();
    const level = levelMap.get(current);

    graph.get(current).forEach((neighbor: any) => {
      inDegree.set(neighbor, inDegree.get(neighbor) - 1);
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor);
        levelMap.set(neighbor, level + 1);
      }
    });
  }

  return levelMap;
}

// Utility to find all prerequisites (recursively) for a given competency
function getAllPrerequisites(
  name: string,
  data: Competency[],
  visited = new Set<string>()
) {
  const node = data.find((c) => c.name === name);
  if (!node || !node.prerequisites) return visited;
  for (const pre of node.prerequisites) {
    if (!visited.has(pre)) {
      visited.add(pre);
      getAllPrerequisites(pre, data, visited);
    }
  }
  return visited;
}

export const PrerequisiteChainModal = ({
  isPrerequisiteModalOpen,
  setIsPrerequisiteModalOpen,
  focusCompetency,
  focusId,
  selectedVersion,
  role,
  setSelectedCompetency,
}: PrerequisiteChainModalProps): JSX.Element => {
  const [data, setData] = useState<Competency[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedNode, setClickedNode] = useState<any>(null);
  const semesterContext = useContext(SemesterContext);
  const userContext = useContext(UserContext);
  const levelMap = useMemo(() => buildDependencyGraph(data), [data]);
  const nodeSpacingX = 250;
  const nodeSpacingY = 60;

  const levels: Record<number, object[]> = {};
  data.forEach((node: any) => {
    const level: number = levelMap.get(node.name) || 0;
    if (!levels[level]) levels[level] = [];
    levels[level].push({ name: node.name, title: node.title });
  });
  // Find all nodes before focusCompetency
  const beforeFocusSet = useMemo(() => {
    if (clickedNode) {
      const set = getAllPrerequisites(clickedNode, data);
      set.add(clickedNode);
      return set;
    } else {
      const set = getAllPrerequisites(focusCompetency, data);
      set.add(focusCompetency);
      return set;
    }
  }, [focusCompetency,clickedNode, data]);

  const nodeTypes = {
    custom: CustomNode,
  };

  ///////////////////
  const nodeWidth = 200;
  const nodeHeight = 10;

  function getLayoutedElements(nodes: any[], edges: any[]) {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: "LR",
      nodesep: nodes.length > 20 ? 50 : 200,
      ranksep: 100,
    });
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - nodeWidth / 2,
          y: nodeWithPosition.y - nodeHeight / 2,
        },
      };
    });

    return { nodes: layoutedNodes, edges };
  }

  ///////////////////

  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    const nodeDefs = data.map((item) => ({
      id: item.name,
      type: "custom",
      data: {
        label: item.name,
        tooltip: item.title,
        backgroundColor: clickedNode
          ? item.name === clickedNode
            ? "#d4a017"
            : beforeFocusSet.has(item.name)
            ? "#0C9B6D"
            : "#14532d"
          : item.name === focusCompetency
          ? "#d4a017"
          : beforeFocusSet.has(item.name)
          ? "#0C9B6D"
          : "#14532d",
        height: clickedNode
          ? item.name === clickedNode
            ? 100
            : 50
          : item.name === focusCompetency
          ? 100
          : 50,
        boxShadow: clickedNode
          ? item.name === clickedNode
            ? "0 4px 4px black"
            : "none"
          : item.name === focusCompetency
          ? "0 4px 4px black"
          : "none",
      },
      position: { x: 0, y: 0 }, // placeholder, will be updated by dagre
      draggable: false,
    }));

    const edgeDefs = data.flatMap((item) =>
      (item.prerequisites || []).map((pre) => ({
        id: `e-${pre}-${item.name}`,
        source: pre,
        target: item.name,
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: true,
        style: {
          stroke: "#ccc", // light gray
          strokeDasharray: "6 4", // 6px line, 4px space
          strokeWidth: 2,
        },
      }))
    );

    return getLayoutedElements(nodeDefs, edgeDefs);
  }, [data, focusCompetency, clickedNode, beforeFocusSet]);

  useEffect(() => {
    getPrerequisiteChain();
  }, [focusId]);

  useEffect(() => {
    if (!clickedNode) {
      setVisibleNodes(layoutedNodes);
      setVisibleEdges(layoutedEdges);
      return;
    }

    const prerequisites = getAllPrerequisites(clickedNode, data);
    const dependents = getAllDependents(clickedNode, data);
    const connectedIds = new Set<string>([
      clickedNode,
      ...Array.from(prerequisites),
      ...Array.from(dependents),
    ]);

    const filteredNodes = layoutedNodes.filter((n: any) =>
      connectedIds.has(n.id)
    );
    const filteredEdges = layoutedEdges.filter(
      (e: any) => connectedIds.has(e.source) && connectedIds.has(e.target)
    );

    setVisibleNodes(filteredNodes);
    setVisibleEdges(filteredEdges);
  }, [clickedNode, layoutedNodes, layoutedEdges, data]);

  const getPrerequisiteChain = () => {
    setLoading(true);

    if (role.role === 1) {
      if (semesterContext.selectedSemester?.label === "All") {
        axios.get(`/version?university_code=CMKL`).then((res) => {
          const version_number = res.data.versions.find(
            (v: any) => v.status === "Active Prime"
          ).version_number;
          axios
            .get(
              `/competency/graph?focus_competency_id=${focusId}&version_number=${version_number}`
            )
            .then((response: any) => {
              setData(response.data);
              setLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching competencies:", error);
              setLoading(false);
            });
        });
      } else {
        axios
          .get(
            `/student/version?student_id=${role.role_id}&semester_name=${semesterContext.selectedSemester?.label}&program_id=${userContext.user?.programs?.[0]?.id}`
          )
          .then((res) => {
            axios
              .get(
                `/competency/graph?focus_competency_id=${focusId}&version_number=${res.data.version.curriculum_version}`
              )
              .then((response: any) => {
                setData(response.data);
                setLoading(false);
              })
              .catch((error) => {
                console.error("Error fetching competencies:", error);
                setLoading(false);
              });
          });
      }
    } else {
      if (selectedVersion !== 0) {
        axios
          .get(
            `/competency/graph?focus_competency_id=${focusId}&version_number=${selectedVersion}`
          )
          .then((response: any) => {
            setData(response.data);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching competencies:", error);
            setLoading(false);
          });
      } else {
        axios.get(`/version?university_code=CMKL`).then((res) => {
          const version_number = res.data.versions.find(
            (v: any) => v.status === "Active Prime"
          ).version_number;
          axios
            .get(
              `/competency/graph?focus_competency_id=${focusId}&version_number=${version_number}`
            )
            .then((response: any) => {
              setData(response.data);
              setLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching competencies:", error);
              setLoading(false);
            });
        });
      }
    }
  };

  const [visibleNodes, setVisibleNodes] = useState(layoutedNodes);
  const [visibleEdges, setVisibleEdges] = useState(layoutedEdges);
  const handleNodeDoubleClick = (event: any, node: any) => {
    setClickedNode(node.id);
  };

  function getAllDependents(
    name: string,
    data: Competency[],
    visited = new Set<string>()
  ): Set<string> {
    const dependents = data.filter((c) =>
      (c.prerequisites || []).includes(name)
    );
    for (const dependent of dependents) {
      if (!visited.has(dependent.name)) {
        visited.add(dependent.name);
        getAllDependents(dependent.name, data, visited);
      }
    }
    return visited;
  }

  const { fitView } = useReactFlow();

  useLayoutEffect(() => {
    if (isPrerequisiteModalOpen && visibleNodes.length > 0) {
      setTimeout(() => {
        fitView({ padding: data?.length < 10 ? 0.3 : 0.05 });
      }, 0);
    }
  }, [isPrerequisiteModalOpen, visibleNodes, data]);

  // Always return a JSX.Element, even if modal is not open
  if (!isPrerequisiteModalOpen) {
    return <></>;
  }

  return (
    <Modal
      onClose={() => {
        setIsPrerequisiteModalOpen(false);
        setVisibleNodes(layoutedNodes);
        setVisibleEdges(layoutedEdges);
        setSelectedCompetency("");
        setClickedNode(null);
      }}
      id="chain_modal"
      isOpen={isPrerequisiteModalOpen}
      setOpen={setIsPrerequisiteModalOpen}
      customStyle={{
        content: {
          maxWidth: "80vw",
          padding: 20,
          background: "#090d20",
          border: "1px solid #2D416C",
          borderRadius: "8px",
          marginTop: 15,
        },
      }}
    >
      <CustomFlex>
        <FlexGrow>
          <Header>
            Prerequisite Chain for {clickedNode || focusCompetency}
          </Header>
        </FlexGrow>

        <ClickableIcon
          name="times"
          onClick={() => {
            setIsPrerequisiteModalOpen(false);
            setVisibleNodes(layoutedNodes);
            setVisibleEdges(layoutedEdges);
            setSelectedCompetency("");
            setClickedNode(null);
          }}
        />
      </CustomFlex>

      <div
        style={{
          height: "80vh",
          background: "#6767671a",
          borderRadius: 5,
          overflow: "auto",
        }}
      >
        {loading ? (
          <div style={{ padding: 10 }}>loading...</div>
        ) : (
          <div style={{ display: "flex", height: "75vh" }}>
            {/* React Flow area, with right margin for controls */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                marginRight: 130,
              }}
            >
              <ReactFlow
                nodeTypes={nodeTypes}
                nodes={visibleNodes.length > 0 ? visibleNodes : layoutedNodes}
                edges={visibleEdges.length ? visibleEdges : layoutedEdges}
                onNodeDoubleClick={handleNodeDoubleClick}
                // onNodesChange={onNodesChange}
                // onEdgesChange={onEdgesChange}
                // onConnect={onConnect}
                fitView
                fitViewOptions={{ padding: data?.length < 10 ? 0.2 : 0.05 }}
                minZoom={0.1}
                proOptions={{ hideAttribution: true }}
                panOnScroll={false}
                zoomOnScroll={true}
                zoomOnPinch={false}
                // panOnDrag={false}
                // nodesDraggable={false}
                nodesConnectable={false}
                selectionOnDrag={false}
                elementsSelectable={false}
                zoomOnDoubleClick={false}
              />
            </div>
            {/* Controls area */}
            <div
              style={{
                width: 150,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                position: "absolute", // optional, if you want it to float
                right: 20,
                top: 70,
                height: "75vh",
                // background: "white", // optional, for contrast
                zIndex: 10,
              }}
            >
              <ZoomControls
                onResetClick={() => {
                  setVisibleNodes(layoutedNodes);
                  setVisibleEdges(layoutedEdges);
                  setClickedNode(null);
                }}
              />{" "}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
